import type { EditorView } from '@milkdown/kit/prose/view';
import type { EditorState } from '@milkdown/kit/prose/state';
import type { Ctx } from '@milkdown/kit/ctx';
import { useMemo, useState, useEffect } from 'react';

import { getLinkAtCursor, type LinkInfo } from './link-utils';

const TOOLTIP_WIDTH = 320;
const TOOLTIP_HEIGHT = 42;
const GAP = 4;

interface TooltipPosition {
  top: number;
  left: number;
}

export interface TooltipState {
  show: boolean;
  position: TooltipPosition;
  linkInfo: LinkInfo | null;
}

const HIDDEN: TooltipState = { show: false, position: { top: 0, left: 0 }, linkInfo: null };

/**
 * Calculate tooltip position above the link, horizontally centered within parent bounds.
 */
function calcTooltipPosition(view: EditorView, linkInfo: LinkInfo): TooltipPosition {
  const startCoords = view.coordsAtPos(linkInfo.from);
  const endCoords = view.coordsAtPos(linkInfo.to);
  const parentRect = view.dom.parentElement!.getBoundingClientRect();

  const selectionCenterX = (startCoords.left + endCoords.left) / 2 - parentRect.left;
  const left = Math.max(0, Math.min(selectionCenterX - TOOLTIP_WIDTH / 2, parentRect.width - TOOLTIP_WIDTH));

  return {
    top: startCoords.top - parentRect.top - TOOLTIP_HEIGHT - GAP,
    left,
  };
}

// Track the link selection range that has been dismissed
interface DismissedSelectionRange {
  from: number;
  to: number;
}

/**
 * Hook for managing link tooltip visibility and positioning.
 * Derives state from editor view - re-computes when selection or document changes.
 */
export function useLinkTooltipState(view: EditorView | undefined, ctx: Ctx | undefined, prevState?: EditorState) {
  // Track dismissed link range - don't re-show while cursor stays within this range
  const [dismissedRange, setDismissedRange] = useState<DismissedSelectionRange | null>(null);

  const dismissTooltip = (linkInfo: LinkInfo | null) => {
    if (linkInfo) {
      setDismissedRange({ from: linkInfo.from, to: linkInfo.to });
    }
  };

  const selectionFrom = view?.state.selection.from ?? 0;
  const selectionTo = view?.state.selection.to ?? 0;

  const tooltipState = useMemo((): TooltipState => {
    if (!view || !ctx) {
      return HIDDEN;
    }

    const linkInfo = getLinkAtCursor(view, ctx);
    const hasTextSelection = selectionFrom !== selectionTo;

    // Hide if no link, or if selecting text over an existing link
    if (!linkInfo || (hasTextSelection && linkInfo.href)) {
      return HIDDEN;
    }

    // Don't show if dismissed and cursor still in dismissed range
    if (dismissedRange) {
      const cursorInDismissedRange = selectionFrom >= dismissedRange.from && selectionTo <= dismissedRange.to;
      if (cursorInDismissedRange) {
        return { ...HIDDEN, linkInfo };
      }
    }

    return { show: true, position: calcTooltipPosition(view, linkInfo), linkInfo };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, ctx, selectionFrom, selectionTo, prevState, dismissedRange]);

  // Clear dismissed range when cursor moves outside it
  useEffect(() => {
    const isCursorOutsideDismissedRange = dismissedRange && (selectionFrom < dismissedRange.from || selectionTo > dismissedRange.to);
    if (isCursorOutsideDismissedRange) {
      setDismissedRange(null);
    }
  }, [dismissedRange, selectionFrom, selectionTo]);

  // Clear dismissed range on any click (re-click to reopen tooltip)
  // Clicking elsewhere moves cursor outside anyway; clicking same link should reopen
  useEffect(() => {
    if (!view || !dismissedRange) return;

    const handleMouseDown = () => setDismissedRange(null);

    view.dom.addEventListener('mousedown', handleMouseDown);

    return () => view.dom.removeEventListener('mousedown', handleMouseDown);
  }, [view, dismissedRange]);

  return { tooltipState, dismissTooltip };
}
